import { TextSelection, type Command } from "@tiptap/pm/state";
import { CellSelection, deleteCellSelection, findCellPos, moveTableColumn, moveTableRow, setCellAttr } from "@tiptap/pm/tables";
import { type Editor, useEditorState } from "@tiptap/react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Columns2,
  Eraser,
  Ellipsis,
  PaintBucket,
  Plus,
  Rows2,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/shared/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";

import { editorBackgroundColors, editorTextColors } from "../../config/colors";
import { getEditorModeCapabilities } from "../../lib/editor-mode";
import { useEditorI18n } from "../../lib/i18n";
import type { EditorContentMode } from "../../types";
import "./table-controls.css";

type TableControlsProps = {
  container: HTMLDivElement | null;
  contentMode: EditorContentMode;
  editor: Editor;
};

type LayoutState = {
  activeCell: HTMLTableCellElement;
  bottomInsert: { left: number; top: number; width: number };
  bottomInsertVisible: boolean;
  isFirstColumn: boolean;
  isFirstRow: boolean;
  leftHandle: { left: number; top: number };
  rightInsert: { left: number; top: number; height: number };
  rightInsertVisible: boolean;
  topHandle: { left: number; top: number };
};

type TableAxis = "column" | "row";
type TableSubmenu = `${TableAxis}-${"align" | "color"}`;

const isTableCell = (element: Element | null): element is HTMLTableCellElement =>
  element instanceof HTMLTableCellElement && (element.tagName === "TD" || element.tagName === "TH");

const buildLayout = (cell: HTMLTableCellElement): LayoutState | null => {
  const table = cell.closest("table");
  const row = cell.closest("tr");

  if (!(table instanceof HTMLTableElement) || !(row instanceof HTMLTableRowElement)) {
    return null;
  }

  const cellRect = cell.getBoundingClientRect();
  const rowRect = row.getBoundingClientRect();
  const tableRect = table.getBoundingClientRect();
  const rowCells = Array.from(row.cells);
  const isFirstColumn = rowCells[0] === cell;
  const isLastColumn = rowCells[rowCells.length - 1] === cell;
  const tableRows = table.tBodies[0]?.rows ?? table.rows;
  const isFirstRow = tableRows[0] === row;
  const isLastRow = tableRows[tableRows.length - 1] === row;
  const tableCenterX = tableRect.left + tableRect.width / 2;
  const tableCenterY = tableRect.top + tableRect.height / 2;

  return {
    activeCell: cell,
    bottomInsert: {
      left: tableCenterX,
      top: tableRect.bottom + 10,
      width: tableRect.width,
    },
    bottomInsertVisible: isLastRow,
    isFirstColumn,
    isFirstRow,
    leftHandle: {
      left: tableRect.left - 8,
      top: rowRect.top + rowRect.height / 2,
    },
    rightInsert: {
      left: tableRect.right + 10,
      top: tableCenterY,
      height: tableRect.height,
    },
    rightInsertVisible: isLastColumn,
    topHandle: {
      left: cellRect.left + cellRect.width / 2,
      top: tableRect.top - 14,
    },
  };
};

const getEditorView = (editor: Editor) => {
  if (editor.isDestroyed) {
    return null;
  }

  try {
    return editor.view;
  } catch {
    return null;
  }
};

const focusCell = (editor: Editor, cell: HTMLTableCellElement | null) => {
  const view = getEditorView(editor);

  if (!cell || !view) {
    return false;
  }

  try {
    const cellPos = view.posAtDOM(cell, 0);
    editor.chain().focus(cellPos + 1).run();
    return true;
  } catch {
    return false;
  }
};

const resolveCellPosition = (editor: Editor, cell: HTMLTableCellElement) => {
  const view = getEditorView(editor);

  if (!view) {
    return null;
  }

  const candidates = [
    view.posAtDOM(cell, 0),
    view.posAtDOM(cell, cell.childNodes.length),
    view.posAtDOM(cell, 0) + 1,
  ];

  for (const pos of candidates) {
    const $cell = findCellPos(editor.state.doc, pos);

    if ($cell) {
      return $cell;
    }
  }

  return null;
};

const selectTableAxis = (editor: Editor, cell: HTMLTableCellElement | null, axis: "column" | "row") => {
  const view = getEditorView(editor);

  if (!cell || !view) {
    return false;
  }

  try {
    const $cell = resolveCellPosition(editor, cell);

    if (!$cell) {
      return focusCell(editor, cell);
    }

    const selection = axis === "column" ? CellSelection.colSelection($cell) : CellSelection.rowSelection($cell);

    view.dispatch(view.state.tr.setSelection(selection).scrollIntoView());
    view.focus();
    return true;
  } catch {
    return focusCell(editor, cell);
  }
};

const getTableAxisInfo = (cell: HTMLTableCellElement | null) => {
  const table = cell?.closest("table");
  const row = cell?.closest("tr");

  if (!cell || !(table instanceof HTMLTableElement) || !(row instanceof HTMLTableRowElement)) {
    return null;
  }

  return {
    columnCount: row.cells.length,
    columnIndex: cell.cellIndex,
    rowCount: table.rows.length,
    rowIndex: Array.from(table.rows).indexOf(row),
  };
};

const isHeaderColumn = (cell: HTMLTableCellElement | null) => {
  const table = cell?.closest("table");

  if (!cell || !(table instanceof HTMLTableElement)) {
    return false;
  }

  return Array.from(table.rows).every((row) => row.cells[cell.cellIndex]?.tagName === "TH");
};

const isHeaderRow = (cell: HTMLTableCellElement | null) => {
  const row = cell?.closest("tr");

  if (!cell || !(row instanceof HTMLTableRowElement)) {
    return false;
  }

  return Array.from(row.cells).every((tableCell) => tableCell.tagName === "TH");
};

const runTableCommand = (editor: Editor, command: Command) => {
  const view = getEditorView(editor);

  if (!view) {
    return false;
  }

  const didRun = command(view.state, view.dispatch);

  if (didRun) {
    view.focus();
  }

  return didRun;
};

const clearTableCellSelection = (editor: Editor) => {
  const view = getEditorView(editor);

  if (!view || !(view.state.selection instanceof CellSelection)) {
    return;
  }

  const { doc, selection, tr } = view.state;
  const focusPos = Math.min(selection.$anchorCell.pos + 1, doc.content.size);

  view.dispatch(tr.setSelection(TextSelection.near(doc.resolve(focusPos), 1)));
};

export function TableControls({ container, contentMode, editor }: TableControlsProps) {
  const t = useEditorI18n();
  const capabilities = getEditorModeCapabilities(contentMode);
  const hoveredLayoutRef = useRef<LayoutState | null>(null);
  const controlsHoveredRef = useRef(false);
  const hideTimeoutRef = useRef<number | null>(null);
  const layoutRafRef = useRef<number | null>(null);
  const columnMenuOpenRef = useRef(false);
  const rowMenuOpenRef = useRef(false);
  const [layout, setLayout] = useState<LayoutState | null>(null);
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const [rowMenuOpen, setRowMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<TableSubmenu | null>(null);
  const activeCell = layout?.activeCell ?? null;

  const { activeAlign } = useEditorState({
    editor,
    selector: () => {
      const cellAlign = activeCell?.getAttribute("data-text-align") || activeCell?.style.textAlign;

      return {
        activeAlign: cellAlign === "center" || cellAlign === "right" ? cellAlign : "left",
      };
    },
  });

  const activeColumnIsHeader = isHeaderColumn(activeCell);
  const activeRowIsHeader = isHeaderRow(activeCell);

  const updateOpenSubmenu = useCallback((submenu: TableSubmenu, open: boolean) => {
    setOpenSubmenu((current) => {
      if (open) {
        return submenu;
      }

      return current === submenu ? null : current;
    });
  }, []);

  const syncHoveredLayout = useCallback(
    (cell: HTMLTableCellElement | null) => {
      if (!container || !editor.isEditable || !getEditorView(editor)) {
        hoveredLayoutRef.current = null;
        setLayout(null);
        return;
      }

      if (!cell) {
        if (!hoveredLayoutRef.current) {
          return;
        }

        hoveredLayoutRef.current = null;

        if (!columnMenuOpen && !rowMenuOpen && !controlsHoveredRef.current) {
          if (hideTimeoutRef.current) {
            window.clearTimeout(hideTimeoutRef.current);
          }

          hideTimeoutRef.current = window.setTimeout(() => {
            if (!controlsHoveredRef.current && !columnMenuOpenRef.current && !rowMenuOpenRef.current) {
              setLayout(null);
            }
          }, 120);
        }

        return;
      }

      if (hoveredLayoutRef.current?.activeCell === cell && cell.isConnected) {
        return;
      }

      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      if (layoutRafRef.current) {
        window.cancelAnimationFrame(layoutRafRef.current);
      }

      layoutRafRef.current = window.requestAnimationFrame(() => {
        layoutRafRef.current = null;

        if (!cell.isConnected) {
          return;
        }

        const nextLayout = buildLayout(cell);
        hoveredLayoutRef.current = nextLayout;

        if (nextLayout) {
          setLayout(nextLayout);
        }
      });
    },
    [columnMenuOpen, container, editor, rowMenuOpen],
  );

  useEffect(() => {
    if (!container) {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      const cell = target?.closest("td, th") ?? null;

      syncHoveredLayout(isTableCell(cell) ? cell : null);
    };

    const handlePointerLeave = () => {
      syncHoveredLayout(null);
    };

    container.addEventListener("pointermove", handlePointerMove);
    container.addEventListener("pointerleave", handlePointerLeave);

    return () => {
      if (layoutRafRef.current) {
        window.cancelAnimationFrame(layoutRafRef.current);
        layoutRafRef.current = null;
      }

      container.removeEventListener("pointermove", handlePointerMove);
      container.removeEventListener("pointerleave", handlePointerLeave);
    };
  }, [container, syncHoveredLayout]);

  useEffect(() => {
    const refresh = () => {
      if (hoveredLayoutRef.current?.activeCell?.isConnected) {
        const nextLayout = buildLayout(hoveredLayoutRef.current.activeCell);
        hoveredLayoutRef.current = nextLayout;
        setLayout(nextLayout);
        return;
      }

      if (!columnMenuOpenRef.current && !rowMenuOpenRef.current && !controlsHoveredRef.current) {
        setLayout(null);
      }
    };

    editor.on("update", refresh);
    window.addEventListener("resize", refresh);
    window.addEventListener("scroll", refresh, true);

    return () => {
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }

      if (layoutRafRef.current) {
        window.cancelAnimationFrame(layoutRafRef.current);
        layoutRafRef.current = null;
      }

      editor.off("update", refresh);
      window.removeEventListener("resize", refresh);
      window.removeEventListener("scroll", refresh, true);
    };
  }, [editor]);

  const keepSelection = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
  };

  const handleControlsPointerEnter = () => {
    controlsHoveredRef.current = true;
  };

  const handleControlsPointerLeave = () => {
    controlsHoveredRef.current = false;

    if (!hoveredLayoutRef.current && !columnMenuOpen && !rowMenuOpen) {
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }

      hideTimeoutRef.current = window.setTimeout(() => {
        if (!controlsHoveredRef.current && !hoveredLayoutRef.current && !columnMenuOpenRef.current && !rowMenuOpenRef.current) {
          setLayout(null);
        }
      }, 120);
    }
  };

  const withActiveCell = (action: () => void) => {
    if (!focusCell(editor, activeCell)) {
      return;
    }

    action();

    window.requestAnimationFrame(() => {
      const refreshedCell =
        hoveredLayoutRef.current?.activeCell && hoveredLayoutRef.current.activeCell.isConnected
          ? hoveredLayoutRef.current.activeCell
          : activeCell;

      if (!refreshedCell?.isConnected) {
        return;
      }

      const nextLayout = buildLayout(refreshedCell);
      hoveredLayoutRef.current = nextLayout;
      setLayout(nextLayout);
    });
  };

  const moveColumn = (direction: -1 | 1) => {
    const axisInfo = getTableAxisInfo(activeCell);

    if (!axisInfo) {
      return;
    }

    const targetIndex = axisInfo.columnIndex + direction;

    if (targetIndex < 0 || targetIndex >= axisInfo.columnCount) {
      return;
    }

    selectTableAxis(editor, activeCell, "column");
    runTableCommand(editor, moveTableColumn({ from: axisInfo.columnIndex, to: targetIndex, select: true }));
  };

  const moveRow = (direction: -1 | 1) => {
    const axisInfo = getTableAxisInfo(activeCell);

    if (!axisInfo) {
      return;
    }

    const targetIndex = axisInfo.rowIndex + direction;

    if (targetIndex < 0 || targetIndex >= axisInfo.rowCount) {
      return;
    }

    selectTableAxis(editor, activeCell, "row");
    runTableCommand(editor, moveTableRow({ from: axisInfo.rowIndex, to: targetIndex, select: true }));
  };

  const closeAxisMenu = (axis: TableAxis) => {
    clearTableCellSelection(editor);
    setOpenSubmenu(null);

    if (axis === "column") {
      columnMenuOpenRef.current = false;
      setColumnMenuOpen(false);
      return;
    }

    rowMenuOpenRef.current = false;
    setRowMenuOpen(false);
  };

  const setAxisColor = (axis: TableAxis, attr: "backgroundColor" | "textColor", value: string) => {
    if (!selectTableAxis(editor, activeCell, axis)) {
      return;
    }

    runTableCommand(editor, setCellAttr(attr, value || null));
    closeAxisMenu(axis);
  };

  const setAxisAlign = (axis: TableAxis, align: "left" | "center" | "right") => {
    if (!selectTableAxis(editor, activeCell, axis)) {
      return;
    }

    runTableCommand(editor, setCellAttr(contentMode === "markdown" ? "align" : "textAlign", align));
    closeAxisMenu(axis);
  };

  const clearAxisContents = (axis: TableAxis) => {
    if (!selectTableAxis(editor, activeCell, axis)) {
      return;
    }

    runTableCommand(editor, deleteCellSelection);
  };

  const renderColorSubmenu = (axis: TableAxis) => (
    <DropdownMenuSub
      open={openSubmenu === `${axis}-color`}
      onOpenChange={(open) => updateOpenSubmenu(`${axis}-color`, open)}
    >
      <DropdownMenuSubTrigger
        onFocus={() => updateOpenSubmenu(`${axis}-color`, true)}
        onPointerEnter={() => updateOpenSubmenu(`${axis}-color`, true)}
      >
        <PaintBucket className="size-4" /> {t("common.color")}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="table-control-menu table-control-color-menu" collisionPadding={16}>
        <div className="table-control-color-label">{t("common.textColor")}</div>
        <div className="table-control-color-grid">
          {editorTextColors.map((color) => (
            <button
              key={`${axis}-text-${color.label}`}
              aria-label={t("colors.textColor", { color: t(color.labelKey) })}
              className="table-control-color-swatch"
              onClick={() => setAxisColor(axis, "textColor", color.value)}
              onMouseDown={keepSelection}
              style={{ color: color.value || "#64748b" }}
              type="button"
            >
              <span>A</span>
            </button>
          ))}
        </div>
        <div className="table-control-color-label">{t("common.backgroundColor")}</div>
        <div className="table-control-color-grid">
          {editorBackgroundColors.map((color) => (
            <button
              key={`${axis}-background-${color.label}`}
              aria-label={t("colors.backgroundColor", { color: t(color.labelKey) })}
              className="table-control-background-swatch"
              onClick={() => setAxisColor(axis, "backgroundColor", color.value)}
              onMouseDown={keepSelection}
              type="button"
            >
              <span style={{ background: color.value || "var(--tt-highlight-default-swatch)" }} />
            </button>
          ))}
        </div>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );

  const renderAlignSubmenu = (axis: TableAxis) => (
    <DropdownMenuSub
      open={openSubmenu === `${axis}-align`}
      onOpenChange={(open) => updateOpenSubmenu(`${axis}-align`, open)}
    >
      <DropdownMenuSubTrigger
        onFocus={() => updateOpenSubmenu(`${axis}-align`, true)}
        onPointerEnter={() => updateOpenSubmenu(`${axis}-align`, true)}
      >
        <AlignLeft className="size-4" /> {t("common.alignment")}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="table-control-menu table-control-submenu w-44" collisionPadding={16}>
        <DropdownMenuItem className={cn(activeAlign === "left" && "table-control-item-active")} onClick={() => setAxisAlign(axis, "left")}>
          <AlignLeft className="size-4" /> {t("common.left")}
        </DropdownMenuItem>
        <DropdownMenuItem className={cn(activeAlign === "center" && "table-control-item-active")} onClick={() => setAxisAlign(axis, "center")}>
          <AlignCenter className="size-4" /> {t("common.center")}
        </DropdownMenuItem>
        <DropdownMenuItem className={cn(activeAlign === "right" && "table-control-item-active")} onClick={() => setAxisAlign(axis, "right")}>
          <AlignRight className="size-4" /> {t("common.right")}
        </DropdownMenuItem>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );

  if (!layout) {
    return null;
  }

  return createPortal(
    <>
      <DropdownMenu
        open={columnMenuOpen}
        onOpenChange={(open) => {
          columnMenuOpenRef.current = open;
          setColumnMenuOpen(open);

          if (open) {
            setOpenSubmenu(null);
            if (hideTimeoutRef.current) {
              window.clearTimeout(hideTimeoutRef.current);
              hideTimeoutRef.current = null;
            }
            selectTableAxis(editor, activeCell, "column");
          }

          if (!open && !rowMenuOpen && !hoveredLayoutRef.current) {
            setOpenSubmenu(null);
            setLayout(null);
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            className="table-control-handle table-control-column"
            onClick={() => selectTableAxis(editor, activeCell, "column")}
            onMouseDown={(event) => {
              keepSelection(event);
              selectTableAxis(editor, activeCell, "column");
            }}
            onPointerEnter={handleControlsPointerEnter}
            onPointerLeave={handleControlsPointerLeave}
            style={{
              left: `${layout.topHandle.left}px`,
              top: `${layout.topHandle.top}px`,
              transform: "translateX(-50%)",
            }}
            type="button"
          >
            <Ellipsis className="table-control-dots-icon" strokeWidth={2.2} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="center"
          className="table-control-menu w-56"
          collisionPadding={16}
          onEscapeKeyDown={() => clearTableCellSelection(editor)}
          onPointerDownOutside={() => clearTableCellSelection(editor)}
          sideOffset={8}
        >
          {capabilities.tableRichFormatting && layout.isFirstColumn && (
            <>
              <DropdownMenuItem
                className={cn(activeColumnIsHeader && "table-control-item-active")}
                onClick={() => withActiveCell(() => editor.chain().focus().toggleHeaderColumn().run())}
              >
                <Columns2 className="size-4" /> {t("table.headerColumn")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem disabled={activeColumnIsHeader} onClick={() => moveColumn(-1)}>
            <ArrowLeft className="size-4" /> {t("table.moveColumnLeft")}
          </DropdownMenuItem>
          <DropdownMenuItem disabled={activeColumnIsHeader} onClick={() => moveColumn(1)}>
            <ArrowRight className="size-4" /> {t("table.moveColumnRight")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled={activeColumnIsHeader} onClick={() => withActiveCell(() => editor.chain().focus().addColumnBefore().run())}>
            <Plus className="size-4" /> {t("table.insertColumnLeft")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => withActiveCell(() => editor.chain().focus().addColumnAfter().run())}>
            <Plus className="size-4" /> {t("table.insertColumnRight")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {capabilities.tableRichFormatting && renderColorSubmenu("column")}
          {capabilities.tableColumnAlignment && renderAlignSubmenu("column")}
          <DropdownMenuItem onClick={() => clearAxisContents("column")}>
            <Eraser className="size-4" /> {t("table.clearColumnContents")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:bg-destructive/10 focus:text-destructive"
            onClick={() => withActiveCell(() => editor.chain().focus().deleteColumn().run())}
          >
            <Trash2 className="size-4" /> {t("table.deleteColumn")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu
        open={rowMenuOpen}
        onOpenChange={(open) => {
          rowMenuOpenRef.current = open;
          setRowMenuOpen(open);

          if (open) {
            setOpenSubmenu(null);
            if (hideTimeoutRef.current) {
              window.clearTimeout(hideTimeoutRef.current);
              hideTimeoutRef.current = null;
            }
            selectTableAxis(editor, activeCell, "row");
          }

          if (!open && !columnMenuOpen && !hoveredLayoutRef.current) {
            setOpenSubmenu(null);
            setLayout(null);
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            className="table-control-handle table-control-row"
            onClick={() => selectTableAxis(editor, activeCell, "row")}
            onMouseDown={(event) => {
              keepSelection(event);
              selectTableAxis(editor, activeCell, "row");
            }}
            onPointerEnter={handleControlsPointerEnter}
            onPointerLeave={handleControlsPointerLeave}
            style={{
              left: `${layout.leftHandle.left}px`,
              top: `${layout.leftHandle.top}px`,
              transform: "translate(-50%, -50%) rotate(90deg)",
            }}
            type="button"
          >
            <Ellipsis className="table-control-dots-icon" strokeWidth={2.2} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="table-control-menu w-56"
          collisionPadding={16}
          onEscapeKeyDown={() => clearTableCellSelection(editor)}
          onPointerDownOutside={() => clearTableCellSelection(editor)}
          sideOffset={8}
        >
          {capabilities.tableRichFormatting && layout.isFirstRow && (
            <>
              <DropdownMenuItem
                className={cn(activeRowIsHeader && "table-control-item-active")}
                onClick={() => withActiveCell(() => editor.chain().focus().toggleHeaderRow().run())}
              >
                <Rows2 className="size-4" /> {t("table.headerRow")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem disabled={activeRowIsHeader} onClick={() => moveRow(-1)}>
            <ArrowUp className="size-4" /> {t("table.moveRowUp")}
          </DropdownMenuItem>
          <DropdownMenuItem disabled={activeRowIsHeader} onClick={() => moveRow(1)}>
            <ArrowDown className="size-4" /> {t("table.moveRowDown")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled={activeRowIsHeader} onClick={() => withActiveCell(() => editor.chain().focus().addRowBefore().run())}>
            <Plus className="size-4" /> {t("table.insertRowAbove")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => withActiveCell(() => editor.chain().focus().addRowAfter().run())}>
            <Plus className="size-4" /> {t("table.insertRowBelow")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {capabilities.tableRichFormatting && renderColorSubmenu("row")}
          {capabilities.tableRichFormatting && renderAlignSubmenu("row")}
          <DropdownMenuItem onClick={() => clearAxisContents("row")}>
            <Eraser className="size-4" /> {t("table.clearRowContents")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:bg-destructive/10 focus:text-destructive"
            onClick={() => withActiveCell(() => editor.chain().focus().deleteRow().run())}
          >
            <Trash2 className="size-4" /> {t("table.deleteRow")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <button
        className="table-control-insert table-control-insert-vertical"
        hidden={!layout.rightInsertVisible}
        onClick={() => withActiveCell(() => editor.chain().focus().addColumnAfter().run())}
        onMouseDown={keepSelection}
        onPointerEnter={handleControlsPointerEnter}
        onPointerLeave={handleControlsPointerLeave}
        style={{
          height: `${layout.rightInsert.height}px`,
          left: `${layout.rightInsert.left}px`,
          top: `${layout.rightInsert.top}px`,
          transform: "translateY(-50%)",
        }}
        type="button"
      >
        <span className="table-control-insert-track">
          <Plus className="size-3" strokeWidth={2.1} />
        </span>
      </button>

      <button
        className="table-control-insert table-control-insert-horizontal"
        hidden={!layout.bottomInsertVisible}
        onClick={() => withActiveCell(() => editor.chain().focus().addRowAfter().run())}
        onMouseDown={keepSelection}
        onPointerEnter={handleControlsPointerEnter}
        onPointerLeave={handleControlsPointerLeave}
        style={{
          left: `${layout.bottomInsert.left}px`,
          top: `${layout.bottomInsert.top}px`,
          transform: "translateX(-50%)",
          width: `${layout.bottomInsert.width}px`,
        }}
        type="button"
      >
        <span className="table-control-insert-track">
          <Plus className="size-3" strokeWidth={2.1} />
        </span>
      </button>
    </>,
    document.body,
  );
}
